// Enumerations
import { AppErrorEnumeration } from 'enumerations/app-error.enumeration';
import { AppStatesEnumeration } from 'enumerations/app-states.enumeration';
import { TimerEnum } from '@openbank/ziniaweb-ui-core-models';
// Resolvers
import { backPreviousStateResolver } from 'resolvers/back-state.resolver';
// Types
import {
  KycBizumBuilderHandlersType,
  KycBizumMethodsHandlersType,
  KycBizumOnErrorBuilderHandlersType,
  KycBizumReceiveBuilderHandlersType,
} from './types/kyc-bizum.handlers.type';
import { GetStateSuccessType } from 'types/get-state-success.type';
// Utilities
import {
  removeBizumTimerFromStorage,
  saveNewBizumTimerOnStorage,
  saveNewBizumTimerOnStorageIfEmpty,
  timerIsFinishedOnStorage,
} from './utils/kyc-bizum.handlers.utils';

const manageReceivedDataFromKycWSEventHandler = ({
  state,
  setCurrentState,
  setError,
}: KycBizumReceiveBuilderHandlersType): void => {
  switch (state) {
    case AppStatesEnumeration.CHECKOUT_SIGNING: // BIZUM OK
      setCurrentState(AppStatesEnumeration.CHECKOUT_SIGNING); // This state is used to show the Bizum signing screen
      break;
    case AppStatesEnumeration.CHECKOUT_LOADING: // BIZUM PRECONTRATO
    case AppStatesEnumeration.CHECKOUT_BIZUM: // CASUISTICA BIZUM HACIENDO POLLING
      break;
    case AppStatesEnumeration.CHECKOUT_IDENTIFICATION: // BIZUM KO
      setError(AppErrorEnumeration.BIZUM_WS_STATUS_REJECTED); // CAMBIAR ERROR
      break;
    default:
      setError(AppErrorEnumeration.STATE_FAILED); // No puede seguir el proceso
  }
};

const onErrorKycBizumWsMessageHandler = ({
  setError,
}: Pick<KycBizumOnErrorBuilderHandlersType, 'setError'>): void => {
  setError(AppErrorEnumeration.BIZUM_WS_ERROR);
};

const updateLocalBizumTimer =
  (): ((timer: number) => number) =>
  (timer: number): number => {
    const newTimer = timer - 1;

    if (timerIsFinishedOnStorage()) {
      return 0;
    } else {
      saveNewBizumTimerOnStorage(newTimer);
      return newTimer;
    }
  };

const createTimerOnStorageAndInitInterval = async ({
  setError,
  setTimer,
}: Pick<KycBizumBuilderHandlersType, 'setError' | 'setTimer'>): Promise<void> => {
  saveNewBizumTimerOnStorageIfEmpty(); // Save timer if not saved
  // Timer finished
  if (timerIsFinishedOnStorage()) {
    removeBizumTimerFromStorage();
    const [success] = await backPreviousStateResolver(); // Return to previous state
    !success && (await backPreviousStateResolver()); // Retry if failed
    setError(AppErrorEnumeration.WS_BIZUM_TIMEOUT);
  } else {
    setTimer(updateLocalBizumTimer());
    setTimeout(() => {
      createTimerOnStorageAndInitInterval({ setError, setTimer });
    }, TimerEnum.TIMEOUT_ONE_SECOND);
  }
};

const setupEnvironmentForKYCBizumEventHandler = ({
  handleUpdateKycHeaderBackClick,
  setOnHeaderBackClick,
  setMainButtonConfiguration,
  ...restProps
}: Omit<KycBizumBuilderHandlersType, 'setCurrentState'>): (() => void) => {
  setMainButtonConfiguration({});
  setOnHeaderBackClick(() => handleUpdateKycHeaderBackClick);
  createTimerOnStorageAndInitInterval(restProps);

  return () => {
    removeBizumTimerFromStorage();
    setOnHeaderBackClick(null);
  };
};

const KycBizumHandlers = ({
  setCurrentState,
  setError,
  ...props
}: KycBizumBuilderHandlersType): KycBizumMethodsHandlersType => ({
  handleOnErrorKycBizumWsMessage: () => onErrorKycBizumWsMessageHandler({ setError }),
  handleOnReceiveKycBizumWsMessage: (wsProps: GetStateSuccessType) =>
    manageReceivedDataFromKycWSEventHandler({ ...wsProps, setCurrentState, setError }),
  handleSetupEnvironmentForKYCBizumEvent: () =>
    setupEnvironmentForKYCBizumEventHandler({ ...props, setError }),
});

export { KycBizumHandlers };
